import React from "react";
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { PlanOption } from "@volatile/pages/OrderTracker/pages/PlanReviewPage/PlanReviewPage";

interface PlanSelectorProps {
  mode: "single" | "comparison";
  // Single mode props
  selectedPlanOption?: string;
  onPlanOptionChange?: (value: string) => void;
  singlePlanTimestamp?: string;
  // Comparison mode props
  selectedPlan?: string;
  selectedPlanAOption?: string;
  selectedPlanBOption?: string;
  onPlanAOptionChange?: (value: string) => void;
  onPlanBOptionChange?: (value: string) => void;
  availablePlanAOptions?: PlanOption[];
  availablePlanBOptions?: PlanOption[];
  planATimestamp?: string;
  planBTimestamp?: string;
  // Shared props
  planOptions: PlanOption[];
  dateLabel?: string;
}

const PlanSelector: React.FC<PlanSelectorProps> = ({
  mode,
  selectedPlanOption,
  onPlanOptionChange,
  singlePlanTimestamp,
  selectedPlan,
  selectedPlanAOption,
  selectedPlanBOption,
  onPlanAOptionChange,
  onPlanBOptionChange,
  availablePlanAOptions,
  availablePlanBOptions,
  planATimestamp,
  planBTimestamp,
  planOptions,
  dateLabel = "Created",
}) => {
  if (mode === "single") {
    return (
      <Stack gap={1} sx={{ width: "calc(50% - 8px)", marginTop: 0 }}>
        <FormControl sx={{ flex: 1 }}>
          <InputLabel id="plan-select-label">Plan</InputLabel>
          <Select
            labelId="plan-select-label"
            id="plan-select"
            value={selectedPlanOption}
            label="Plan"
            onChange={(e) => onPlanOptionChange?.(e.target.value)}
            startAdornment={<SearchIcon />}
          >
            {planOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary">
          {dateLabel}: {singlePlanTimestamp || "Mar-17-2024, 3:44 PM"}
        </Typography>
      </Stack>
    );
  }

  if (mode === "comparison") {
    return (
      <Stack direction="row" gap={2} sx={{ marginTop: 0 }}>
        <Stack gap={1} sx={{ flex: 1 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="plan-a-label">Plan A</InputLabel>
            <Select
              labelId="plan-a-label"
              value={selectedPlanAOption}
              onChange={(e) => onPlanAOptionChange?.(e.target.value)}
              label="Plan A"
              startAdornment={<SearchIcon />}
            >
              {availablePlanAOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ minHeight: 24 }}
          >
            <Typography variant="caption" color="text.secondary">
              {dateLabel}: {planATimestamp || "Mar-16-2024, 1:30 PM"}
            </Typography>
            {selectedPlan === "planA" && (
              <Chip
                label="Selected"
                color="success"
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  "& .MuiChip-label": {
                    color: "black",
                  },
                }}
              />
            )}
          </Stack>
        </Stack>

        <Stack gap={1} sx={{ flex: 1 }}>
          <FormControl sx={{ flex: 1 }}>
            <InputLabel id="plan-b-label">Plan B</InputLabel>
            <Select
              labelId="plan-b-label"
              value={selectedPlanBOption}
              onChange={(e) => onPlanBOptionChange?.(e.target.value)}
              label="Plan B"
              startAdornment={<SearchIcon />}
            >
              {availablePlanBOptions?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            sx={{ minHeight: 24 }}
          >
            <Typography variant="caption" color="text.secondary">
              {dateLabel}: {planBTimestamp || "Mar-17-2024, 3:44 PM"}
            </Typography>
            {selectedPlan === "planB" && (
              <Chip
                label="Selected"
                color="success"
                size="small"
                sx={{
                  fontSize: "0.7rem",
                  "& .MuiChip-label": {
                    color: "black",
                  },
                }}
              />
            )}
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return null;
};

export default PlanSelector;
